/**
 * `matchMedia` snapshot that ignores URL-bar / visualViewport height churn.
 *
 * Latch resets when:
 * - `screen.orientation.angle` (or legacy `window.orientation`) actually
 *   changes — some Android Chrome builds fire `orientationchange` on toolbar
 *   show/hide without a real rotate, which used to clear the latch and re-read
 *   live height queries (narrow wipe / asset swap). Short-desktop stacked mode
 *   uses a live MQ instead (`useHubShortViewport`) — it is already `min-width`
 *   gated off phones.
 * - `window.innerWidth` changes — DevTools / WP preview size swaps, desktop
 *   window resize, and split-screen. Height-only resize (URL bar) leaves the
 *   latch alone so we do not remount mobile↔desktop videos mid-read.
 *
 * After a real rotate, Android often delivers `orientationchange` *before*
 * `matchMedia` flips. Re-latching on that first read stuck the hub on the
 * pre-rotate layout when returning to portrait. We resample after paint and
 * allow MQ updates for a short settle window. Width swaps use the same settle
 * path so assets / wipe mode catch up with the new frame.
 */

type Latch = {
  value: boolean;
  query: string;
};

const latches = new Map<string, Latch>();

/** While `performance.now() < mqRefreshUntil`, MQ changes may replace the latch. */
let mqRefreshUntil = 0;
let latchEpoch = 0;

function readMatches(query: string): boolean {
  return window.matchMedia(query).matches;
}

function readOrientationAngle(): number {
  const modern = window.screen?.orientation?.angle;
  if (typeof modern === "number") {
    return modern;
  }
  const legacy = (window as Window & { orientation?: number }).orientation;
  return typeof legacy === "number" ? legacy : 0;
}

function readLayoutWidth(): number {
  return window.innerWidth;
}

let lastOrientationAngle: number | null = null;
let lastLayoutWidth: number | null = null;
let viewportListening = false;
const viewportListeners = new Set<() => void>();

function resetAllLatches(): void {
  latches.clear();
}

function notifyViewportListeners(): void {
  viewportListeners.forEach((listener) => listener());
}

/** Clear latches and resample through a short settle window. */
function beginLatchRefresh(): void {
  latchEpoch += 1;
  const epoch = latchEpoch;
  resetAllLatches();
  // Let the next MQ / deferred samples replace whatever we read too early.
  mqRefreshUntil = performance.now() + 600;
  notifyViewportListeners();

  // Viewport / matchMedia often lag the trigger by a frame or two.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (epoch !== latchEpoch) {
        return;
      }
      resetAllLatches();
      notifyViewportListeners();
    });
  });
  window.setTimeout(() => {
    if (epoch !== latchEpoch) {
      return;
    }
    resetAllLatches();
    notifyViewportListeners();
  }, 300);
}

function onRealOrientationChange(): void {
  const next = readOrientationAngle();
  if (lastOrientationAngle !== null && next === lastOrientationAngle) {
    // Toolbar / visualViewport churn — ignore.
    return;
  }
  lastOrientationAngle = next;
  // Rotate usually changes width too; keep the width sample in sync so the
  // resize listener does not double-fire a second refresh.
  lastLayoutWidth = readLayoutWidth();
  beginLatchRefresh();
}

function onLayoutWidthMaybeChanged(): void {
  const next = readLayoutWidth();
  if (lastLayoutWidth === next) {
    // Height-only churn (URL bar) — ignore.
    return;
  }
  const hadWidth = lastLayoutWidth !== null;
  lastLayoutWidth = next;
  if (!hadWidth) {
    // Initial sample from ensureViewportReset — do not notify.
    return;
  }
  beginLatchRefresh();
}

function ensureViewportReset(): void {
  if (viewportListening || typeof window === "undefined") {
    return;
  }
  lastOrientationAngle = readOrientationAngle();
  lastLayoutWidth = readLayoutWidth();
  window.addEventListener("orientationchange", onRealOrientationChange);
  const screenOrientation = window.screen?.orientation;
  if (screenOrientation && typeof screenOrientation.addEventListener === "function") {
    screenOrientation.addEventListener("change", onRealOrientationChange);
  }
  // Width-only signal: DevTools device mode, WP preview presets, desktop
  // resize. Prefer `innerWidth` over visualViewport — zoom/URL-bar should not
  // count as a layout swap.
  window.addEventListener("resize", onLayoutWidthMaybeChanged);
  viewportListening = true;
}

/**
 * Subscribe to the live media query and to viewport resets so React re-reads
 * the (possibly new) latch.
 */
export function subscribeLatchedMatchMedia(
  query: string,
  onStoreChange: () => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  ensureViewportReset();
  const mediaQuery = window.matchMedia(query);
  const onMq = () => {
    const settling = performance.now() < mqRefreshUntil;
    if (!latches.has(query)) {
      onStoreChange();
      return;
    }
    // After a real rotate / width swap, accept MQ flips so we do not stay
    // latched on the pre-change reading. Ignore MQ churn once settled —
    // that was the URL-bar false orientation path.
    if (settling) {
      const live = mediaQuery.matches;
      const latched = latches.get(query);
      if (latched && latched.value !== live) {
        latches.delete(query);
        onStoreChange();
      }
    }
  };
  viewportListeners.add(onStoreChange);
  mediaQuery.addEventListener("change", onMq);
  return () => {
    mediaQuery.removeEventListener("change", onMq);
    viewportListeners.delete(onStoreChange);
  };
}

export function getLatchedMatchMediaSnapshot(query: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  ensureViewportReset();
  const existing = latches.get(query);
  if (existing) {
    return existing.value;
  }
  const value = readMatches(query);
  latches.set(query, { value, query });
  return value;
}
